import React from 'react';

import { ErrorBoundary } from '@atlassian/twg-common/error-boundary';

import type { ExtrasAndTags } from './types';
import { logException } from './utils';

export type SentryErrorBoundaryProps = {
	/**
	 * The Atlassian account ID of the current user, attached to the Sentry event for triage.
	 */
	accountId?: string;
	/**
	 * Fallback UI rendered when a child component throws.
	 */
	fallback: React.ReactNode;
	/**
	 * Identifies the source of the error — first layer of differentiation.
	 */
	loggerName?: string;
	/**
	 * Optional additional error handler called alongside Sentry capture.
	 */
	onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
	/**
	 * Team name for Sentry issue assignment.
	 * Must exist in https://atlassian-2y.sentry.io/settings/teams/
	 */
	teamName?: string;
	/**
	 * Second layer of differentiation for reusable components sharing a `loggerName`.
	 */
	touchpointId?: string;
	children: React.ReactNode;
};

export const SentryErrorBoundary = ({
	children,
	fallback,
	loggerName,
	accountId,
	onError,
	teamName,
	touchpointId,
}: SentryErrorBoundaryProps): JSX.Element => {
	return (
		<ErrorBoundary
			fallback={fallback}
			onError={(error, errorInfo) => {
				const extras: ExtrasAndTags = {
					loggerName,
					accountId,
					teamName,
					touchpointId,
				};
				logException(error, extras);
				onError?.(error, errorInfo);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};
